import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { AppColors } from "@/styles/colors";
import { Flex, Grid, Spinner } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useWatch } from "react-hook-form";
import { logisticDaysSchema, monthNames } from "./schema/logistic.schema";
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { useRoles } from "@/hooks/useRoles";
import {
  buildYearOptionsHandler,
  fetchActiveTrucksOptionsHandler,
  fetchLogisticDaysHandler,
  fetchModuleTypesOptionsHandler,
  patchLogisticDaysHandler,
  prepareDaysForFetch,
  SelectOption,
  setBlockedDaysHandler,
} from "./schema/handler-functions";
import { LogisticAdditionalButtons } from "./view/LogisticAdditionalButtons";
import { SearchSelectorForm } from "./form/SearchSelectorForm";
import { PendingDataView } from "./view/PendingDataView";
import { MonthGridItem } from "./view/MonthGridItem";
import { PermissionCode } from "@/constants/permissions";

export const LogisticHandlingPage = () => {
  const [isActive, setIsActive] = useState(false);
  const [openReasonsModal, setOpenReasonsModal] = useState(false);
  const [saving, setSaving] = useState(false);

  const navigate = useNavigate();
  const { fetchPermissions } = useRoles();

  useEffect(() => {
    setIsActive(true);
  }, []);

  const { control, setValue } = useForm({
    resolver: zodResolver(logisticDaysSchema),
  });

  const [yearOptions, setYearOptions] = useState<SelectOption[]>([]);
  const [truckOptions, setTruckOptions] = useState<SelectOption[]>([]);
  const [moduleTypeOptions, setModuleTypeOptions] = useState<SelectOption[]>(
    [],
  );

  const [loadingTrucks, setLoadingTrucks] = useState(false);
  const [loadingModuleTypes, setLoadingModuleTypes] = useState(false);
  const [loadingDays, setLoadingDays] = useState(false);
  const [days, setDays] = useState<any[]>([]);

  const lastFetchKeyRef = useRef<string | null>(null);
  const lastTiposKeyRef = useRef<string | null>(null);

  const initialBlockedDatesRef = useRef<Set<string>>(new Set());
  const initialReasonsRef = useRef<Map<string, string>>(new Map());
  const [currentBlockedDates, setCurrentBlockedDates] = useState<Set<string>>(
    new Set(),
  );
  const { hasWriteAccess } = useRoles();

  const canToggleDays = useMemo(() => {
    return !!hasWriteAccess(PermissionCode.LOGISTIC);
  }, [hasWriteAccess]);

  useEffect(() => {
    if (!isActive) return;

    buildYearOptionsHandler({
      setYearOptions,
      setDefaultYearValue: (v) => setValue("year", v),
      yearsAhead: 2,
    });

    fetchModuleTypesOptionsHandler({
      setLoadingModuleTypes,
      setModuleTypeOptions,
      navigate,
      fetchPermissions,
    });
  }, [isActive]);

  const yearRaw = useWatch({ control, name: "year" });
  const equipoRaw = useWatch({ control, name: "idEquipo" });
  const tipoRaw = useWatch({ control, name: "idTipoModulo" });

  const selectedValues = useMemo(() => {
    const yearSelected = Array.isArray(yearRaw) ? yearRaw?.[0] : yearRaw;
    const equipoSelected = Array.isArray(equipoRaw)
      ? equipoRaw?.[0]
      : equipoRaw;
    const tipoSelected = Array.isArray(tipoRaw) ? tipoRaw?.[0] : tipoRaw;

    const year = Number(yearSelected);
    const idEquipo = Number(equipoSelected);
    const tipoNumero = Number(tipoSelected);
    const idTipoModulo = tipoNumero;

    const hasAll =
      Number.isFinite(year) &&
      Number.isFinite(idEquipo) &&
      Number.isFinite(idTipoModulo);

    return {
      year,
      idEquipo,
      idTipoModulo,
      hasAll,
    };
  }, [yearRaw, equipoRaw, tipoRaw]);

  const tiposKey = useMemo(() => {
    return Number.isFinite(selectedValues.idTipoModulo)
      ? String(selectedValues.idTipoModulo)
      : "";
  }, [selectedValues.idTipoModulo]);

  const tipoModuloArr = useMemo(() => {
    return Number.isFinite(selectedValues.idTipoModulo)
      ? [selectedValues.idTipoModulo]
      : undefined;
  }, [selectedValues.idTipoModulo]);

  useEffect(() => {
    if (!isActive) return;

    if (lastTiposKeyRef.current === tiposKey) return;
    lastTiposKeyRef.current = tiposKey;

    setValue("idEquipo", [] as any);
    setTruckOptions([]);

    if (!tiposKey) {
      setLoadingTrucks(false);
      return;
    }

    fetchActiveTrucksOptionsHandler({
      setLoadingTrucks,
      setTruckOptions,
      tipoModulo: tipoModuloArr,
      navigate,
      fetchPermissions,
    });
  }, [isActive, tiposKey, tipoModuloArr]);

  const toggleBlockedDate = useCallback((dateKey: string) => {
    setCurrentBlockedDates((prev) => {
      const next = new Set(prev);
      if (next.has(dateKey)) next.delete(dateKey);
      else next.add(dateKey);
      return next;
    });
  }, []);

  const hasChanges = useMemo(() => {
    const a = initialBlockedDatesRef.current;
    const b = currentBlockedDates;
    if (a.size !== b.size) return true;
    for (const k of a) {
      if (!b.has(k)) return true;
    }
    return false;
  }, [currentBlockedDates]);

  const datesToBlock = useMemo(() => {
    const initial = initialBlockedDatesRef.current;
    const result: string[] = [];
    for (const d of currentBlockedDates) {
      if (!initial.has(d)) result.push(d);
    }
    return result.sort((a, b) => a.localeCompare(b));
  }, [currentBlockedDates]);

  const datesToUnblock = useMemo(() => {
    const initial = initialBlockedDatesRef.current;
    const result: string[] = [];
    for (const d of initial) {
      if (!currentBlockedDates.has(d)) result.push(d);
    }
    return result.sort((a, b) => a.localeCompare(b));
  }, [currentBlockedDates]);

  const handleOpenSaveModal = useCallback(() => {
    if (loadingDays || !hasChanges) return;
    setOpenReasonsModal(true);
  }, [hasChanges, loadingDays]);

  const handleConfirmSave = useCallback(
    async (reasonsByDate: Record<string, string>) => {
      const toDateKey = (dateKey: string) => {
        const key = String(dateKey);
        return key.includes("T") ? key.split("T")[0] : key;
      };

      const blockedDates = Array.from(currentBlockedDates).sort((a, b) =>
        a.localeCompare(b),
      );

      const diasABloquear = blockedDates.map((dateKey) => {
        const fromModal = String(reasonsByDate?.[dateKey] ?? "").trim();
        const fromInitial = String(
          initialReasonsRef.current.get(dateKey) ?? "",
        ).trim();
        const motivoBloqueo = fromModal || fromInitial;

        return {
          fecha: toDateKey(dateKey),
          idEquipo: selectedValues.idEquipo,
          idTipoModulo: selectedValues.idTipoModulo,
          motivoBloqueo,
        };
      });

      try {
        setSaving(true);
        await patchLogisticDaysHandler({
          props: {
            diasABloquear,
          },
          navigate,
          fetchPermissions,
        });

        fetchLogisticDaysHandler({
          props: {
            year: selectedValues.year,
            idEquipo: selectedValues.idEquipo,
            idTipoModulo: selectedValues.idTipoModulo,
          },
          setLoadingDays,
          setDays,
          navigate,
          fetchPermissions,
          showLoad: false,
        });
      } finally {
        setSaving(false);
      }
    },
    [
      currentBlockedDates,
      selectedValues.idEquipo,
      selectedValues.idTipoModulo,
      selectedValues.year,
      navigate,
      fetchPermissions,
      setSaving,
    ],
  );

  const buildMonthGrid = useCallback((year: number, monthIndex: number) => {
    const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
    const cells: Array<number | null> = [];
    for (let d = 1; d <= daysInMonth; d += 1) cells.push(d);
    const remainder = cells.length % 7;
    if (remainder !== 0) {
      const blanksAfter = 7 - remainder;
      for (let i = 0; i < blanksAfter; i += 1) cells.push(null);
    }
    return cells;
  }, []);

  const monthCells = useMemo(() => {
    if (!Number.isFinite(selectedValues.year))
      return [] as Array<Array<number | null>>;
    return monthNames.map((_, monthIndex) =>
      buildMonthGrid(selectedValues.year, monthIndex),
    );
  }, [selectedValues.year, monthNames]);

  useEffect(() => {
    if (!isActive) return;

    const shouldFetch = prepareDaysForFetch({
      selectedValues,
      lastFetchKeyRef,
      setDays,
      initialBlockedDatesRef,
      initialReasonsRef,
      setCurrentBlockedDates,
      tiposKey,
    });

    if (!shouldFetch) return;

    fetchLogisticDaysHandler({
      props: {
        year: selectedValues.year,
        idEquipo: selectedValues.idEquipo,
        idTipoModulo: selectedValues.idTipoModulo,
      },
      setLoadingDays,
      setDays,
      navigate,
      fetchPermissions,
    });
  }, [
    isActive,
    selectedValues.hasAll,
    selectedValues.year,
    selectedValues.idEquipo,
    selectedValues.idTipoModulo,
    tiposKey,
    fetchPermissions,
    navigate,
  ]);

  useEffect(() => {
    setBlockedDaysHandler({
      days,
      initialBlockedDatesRef,
      initialReasonsRef,
      selectedValues,
      loadingDays,
      setCurrentBlockedDates,
    });
  }, [days, loadingDays, selectedValues.hasAll]);

  return (
    <Flex
      flexDir="column"
      paddingTop="20px"
      paddingBottom="20px"
      data-testid="logistic-handling-page"
      height="100vh"
      overflowY="hidden"
    >
      <SectionHeader
        title="Logística completa"
        aditionalButtons={
          hasWriteAccess(PermissionCode.LOGISTIC) && (
            <LogisticAdditionalButtons
              loadingDays={loadingDays}
              hasChanges={hasChanges}
              saving={saving}
              setCurrentBlockedDates={setCurrentBlockedDates}
              initialBlockedDatesRef={initialBlockedDatesRef}
              onSave={handleOpenSaveModal}
            />
          )
        }
      />
      <SearchSelectorForm
        yearOptions={yearOptions}
        moduleTypeOptions={moduleTypeOptions}
        truckOptions={truckOptions}
        control={control}
        loadingModuleTypes={loadingModuleTypes}
        loadingTrucks={loadingTrucks}
      />

      {!selectedValues.hasAll && <PendingDataView />}

      {selectedValues.hasAll && (
        <Flex
          marginTop="16px"
          width="100%"
          flex="1"
          minHeight="calc(100vh - 220px)"
          flexDir="column"
          gap="16px"
          overflowY="auto"
        >
          {loadingDays && (
            <Flex
              width="100%"
              flex="1"
              minHeight="100%"
              borderRadius="16px"
              border={`1px solid ${AppColors.WHITE_BORDER_1}`}
              alignItems="center"
              justifyContent="center"
            >
              <Spinner size="lg" />
            </Flex>
          )}

          {!loadingDays && (
            <Grid templateColumns="repeat(4, 1fr)" gap="16px">
              {monthNames.map((monthName, monthIndex) => {
                const cells = monthCells?.[monthIndex] ?? [];
                return (
                  <MonthGridItem
                    key={monthName}
                    monthName={monthName}
                    cells={cells}
                    monthIndex={monthIndex}
                    selectedValues={selectedValues}
                    currentBlockedDates={currentBlockedDates}
                    initialReasonsRef={initialReasonsRef}
                    toggleBlockedDate={toggleBlockedDate}
                    canToggle={canToggleDays}
                  />
                );
              })}
            </Grid>
          )}
        </Flex>
      )}

      <Flex paddingTop="12px" data-testid="logistic-days-count" display="none">
        {String(days.length)}
      </Flex>

      <Suspense>
        <BlockReasonsModalDynamic
          open={openReasonsModal}
          setOpen={setOpenReasonsModal}
          datesToBlock={datesToBlock}
          datesToUnblock={datesToUnblock}
          saving={saving}
          onConfirm={handleConfirmSave}
        />
      </Suspense>
    </Flex>
  );
};

const BlockReasonsModalDynamic = lazy(() =>
  import("./modal/BlockReasonsModal").then((el) => ({
    default: el["BlockReasonsModal"],
  })),
);
